import type { RightPanelSurface } from "./rightPanelStore";
import { rightPanelPullRequestSurfaceId, rightPanelWorkflowSurfaceId } from "./rightPanelStore";
import {
  RESOURCE_RELEASE_REASONS,
  type ResourceReleaseReason,
} from "@afar-research/shared/resourceLedger";
import type { EnvironmentId } from "@afar-research/contracts";
import { acquireDesktopTab } from "./browser/desktopTabLifetime";
import { readEnvironmentHostsBrowser } from "./state/environments";
import {
  RightPanelSurfaceContent,
  RightPanelSurfaceUnavailable,
} from "./components/RightPanelSurfaceContent";
import { workSurfaceResources } from "./workSurfaceResources";
import { workspaceSurfaceResourceOwnerIdentity } from "./workspaceSurfaceHost";
import type {
  WorkspaceSurfaceAcquireInput,
  WorkspaceSurfaceDefinition,
  WorkspaceSurfaceLease,
} from "./workspaceSurfaceHost";

/**
 * What a surface renders when no bespoke renderer claims its kind: the content the chat view
 * registered for it, and failing that an explicit message. A Region rendered with nothing at all
 * presents as a blank body under its own tab, which reads as the panel being broken rather than
 * as a surface nothing can fill, so no right-panel surface is ever left to render empty.
 */
const renderRightPanelRegion: WorkspaceSurfaceDefinition<RightPanelSurface>["render"] = ({
  descriptor,
  renderRegion,
}) =>
  renderRegion(
    <RightPanelSurfaceContent
      descriptor={descriptor}
      fallback={<RightPanelSurfaceUnavailable />}
    />,
  );

export type RightPanelSurfaceRenderers = Partial<
  Readonly<
    Record<RightPanelSurface["kind"], WorkspaceSurfaceDefinition<RightPanelSurface>["render"]>
  >
>;

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

export function decodeRightPanelSurface(value: unknown): RightPanelSurface | null {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.kind !== "string") {
    return null;
  }
  switch (value.kind) {
    case "preview":
      if (value.resourceId === null && value.id === "browser:new") {
        return { id: "browser:new", kind: "preview", resourceId: null };
      }
      return typeof value.resourceId === "string" && value.id === `browser:${value.resourceId}`
        ? { id: value.id as `browser:${string}`, kind: "preview", resourceId: value.resourceId }
        : null;
    case "terminal":
      return typeof value.resourceId === "string" &&
        value.id === `terminal:${value.resourceId}` &&
        isStringArray(value.terminalIds) &&
        value.terminalIds.length > 0 &&
        typeof value.activeTerminalId === "string" &&
        value.terminalIds.includes(value.activeTerminalId) &&
        (value.splitDirection === undefined ||
          value.splitDirection === "horizontal" ||
          value.splitDirection === "vertical")
        ? {
            id: value.id as `terminal:${string}`,
            kind: "terminal",
            resourceId: value.resourceId,
            terminalIds: value.terminalIds,
            activeTerminalId: value.activeTerminalId,
            ...(value.splitDirection === undefined ? {} : { splitDirection: value.splitDirection }),
          }
        : null;
    case "file":
      return typeof value.relativePath === "string" &&
        value.id === `file:${value.relativePath}` &&
        (value.revealLine === null ||
          (typeof value.revealLine === "number" && Number.isFinite(value.revealLine))) &&
        typeof value.revealRequestId === "number" &&
        Number.isSafeInteger(value.revealRequestId) &&
        value.revealRequestId >= 0
        ? {
            id: value.id as `file:${string}`,
            kind: "file",
            relativePath: value.relativePath,
            revealLine: value.revealLine,
            revealRequestId: value.revealRequestId,
          }
        : null;
    case "tasks":
      return value.id === "tasks" &&
        (value.resourceId === null || typeof value.resourceId === "string")
        ? { id: "tasks", kind: "tasks", resourceId: value.resourceId }
        : null;
    case "agent":
      return typeof value.agentId === "string" && value.id === `agent:${value.agentId}`
        ? { id: value.id as `agent:${string}`, kind: "agent", agentId: value.agentId }
        : null;
    case "diff":
      return value.id === "diff" ? { id: "diff", kind: "diff" } : null;
    case "files":
      return value.id === "files" ? { id: "files", kind: "files" } : null;
    case "experiment":
      return value.id === "experiments" &&
        (value.experimentId === null ||
          (typeof value.experimentId === "string" && value.experimentId.length > 0))
        ? { id: "experiments", kind: "experiment", experimentId: value.experimentId }
        : null;
    case "pull-requests":
      return value.id === "pull-requests" ? { id: "pull-requests", kind: "pull-requests" } : null;
    case "contributions":
      return value.id === "contributions" ? { id: "contributions", kind: "contributions" } : null;
    case "screen":
      return value.id === "screen" ? { id: "screen", kind: "screen" } : null;
    case "pull-request": {
      const { id, projectId, host, repository, number, url } = value;
      if (
        typeof id !== "string" ||
        !id.startsWith("pull-request:") ||
        typeof projectId !== "string" ||
        typeof repository !== "string" ||
        repository.trim().length === 0 ||
        typeof number !== "number" ||
        !Number.isSafeInteger(number) ||
        number <= 0 ||
        typeof url !== "string" ||
        (host !== undefined && typeof host !== "string")
      ) {
        return null;
      }
      const target = {
        projectId,
        ...(typeof host === "string" ? { host } : {}),
        repository,
        number,
        url,
      };
      // A restored descriptor is a locator: it is rebuilt from its own parts so a stored id can
      // never address something other than what those parts say.
      return { ...target, id: rightPanelPullRequestSurfaceId(target), kind: "pull-request" };
    }
    case "workflow":
      return typeof value.workflowName === "string" &&
        value.workflowName.length > 0 &&
        value.id === rightPanelWorkflowSurfaceId(value.workflowName)
        ? {
            id: rightPanelWorkflowSurfaceId(value.workflowName),
            kind: "workflow",
            workflowName: value.workflowName,
          }
        : null;
    default:
      return null;
  }
}

function acquireRightPanelSurfaceLease(
  lifetime: "open-owner" | "retained-owner",
): (input: WorkspaceSurfaceAcquireInput<RightPanelSurface>) => WorkspaceSurfaceLease {
  return ({ descriptor, scope }) =>
    workSurfaceResources.acquire({
      owner: {
        kind: "workspace-surface",
        identity: workspaceSurfaceResourceOwnerIdentity(scope, descriptor),
      },
      resource: { kind: "surface-lease", identity: "host" },
      lifetime,
    });
}

const acquireOpenSurfaceLease = acquireRightPanelSurfaceLease("open-owner");
const acquireRetainedSurfaceLease = acquireRightPanelSurfaceLease("retained-owner");

function abortedReleaseReason(signal: AbortSignal): ResourceReleaseReason {
  return RESOURCE_RELEASE_REASONS.includes(signal.reason as ResourceReleaseReason)
    ? (signal.reason as ResourceReleaseReason)
    : "replacement";
}

const acquireBrowserSurfaceLease = async (
  input: WorkspaceSurfaceAcquireInput<RightPanelSurface>,
): Promise<WorkspaceSurfaceLease> => {
  const hostLease = acquireRetainedSurfaceLease(input);
  if (input.descriptor.kind !== "preview" || input.descriptor.resourceId === null) {
    return hostLease;
  }
  if (input.scope.type !== "thread") {
    hostLease.release("failure");
    throw new Error("Browser workspace surfaces require a thread.");
  }
  // The Environment runs this tab's browser itself (ADR-0137); there is no Desktop guest to create.
  if (readEnvironmentHostsBrowser(input.scope.workspace.environmentId as EnvironmentId)) {
    return hostLease;
  }
  let desktopTab: ReturnType<typeof acquireDesktopTab>;
  try {
    desktopTab = acquireDesktopTab(input.descriptor.resourceId);
  } catch (cause) {
    hostLease.release("failure");
    throw cause;
  }
  let released = false;
  const release = (reason: ResourceReleaseReason): void => {
    if (released) return;
    released = true;
    desktopTab.release();
    hostLease.release(reason);
  };
  let removeAbortListener: () => void = () => undefined;
  const aborted = new Promise<never>((_resolve, reject) => {
    const handleAbort = () => {
      release(abortedReleaseReason(input.signal));
      reject(new DOMException("Workspace surface acquisition aborted.", "AbortError"));
    };
    if (input.signal.aborted) {
      handleAbort();
      return;
    }
    input.signal.addEventListener("abort", handleAbort, { once: true });
    removeAbortListener = () => input.signal.removeEventListener("abort", handleAbort);
  });
  try {
    await Promise.race([desktopTab.ready, aborted]);
  } catch (cause) {
    if (!input.signal.aborted) release("failure");
    throw cause;
  } finally {
    removeAbortListener();
  }
  return { release };
};

/**
 * Declarative policy for the existing right-panel descriptors.
 *
 * Feature state and actions remain with their feature modules. The surface host
 * uses this inventory for scope, presentation persistence, renderer lifetime,
 * and bounded opaque lease ownership.
 */
export function createRightPanelSurfaceDefinitions(
  renderers: RightPanelSurfaceRenderers = {},
): readonly WorkspaceSurfaceDefinition<RightPanelSurface>[] {
  return [
    {
      kind: "tasks",
      scope: "thread",
      identityVersion: 1,
      persistence: "none",
      lifetime: "active",
      decode: decodeRightPanelSurface,
      acquire: acquireOpenSurfaceLease,
      render: renderers.tasks ?? renderRightPanelRegion,
    },
    {
      kind: "agent",
      scope: "thread",
      identityVersion: 1,
      persistence: "none",
      lifetime: "active",
      decode: decodeRightPanelSurface,
      acquire: acquireOpenSurfaceLease,
      render: renderers.agent ?? renderRightPanelRegion,
    },
    {
      kind: "preview",
      scope: "thread",
      identityVersion: 1,
      persistence: "session",
      lifetime: "retained",
      retentionLimit: 3,
      decode: decodeRightPanelSurface,
      acquire: acquireBrowserSurfaceLease,
      render: renderers.preview ?? renderRightPanelRegion,
    },
    {
      kind: "terminal",
      scope: "thread",
      identityVersion: 1,
      persistence: "session",
      lifetime: "active",
      decode: decodeRightPanelSurface,
      acquire: acquireOpenSurfaceLease,
      render: renderers.terminal ?? renderRightPanelRegion,
    },
    {
      kind: "file",
      scope: "thread",
      identityVersion: 1,
      persistence: "session",
      lifetime: "active",
      decode: decodeRightPanelSurface,
      acquire: acquireOpenSurfaceLease,
      render: renderers.file ?? renderers.files ?? renderRightPanelRegion,
    },
    // Rendered only while active, so the screen stream runs only while someone looks.
    ...(["diff", "pull-requests", "contributions", "screen"] as const).map(
      (kind) =>
        ({
          kind,
          scope: "thread",
          identityVersion: 1,
          persistence: "device",
          lifetime: "active",
          decode: decodeRightPanelSurface,
          acquire: acquireOpenSurfaceLease,
          render: renderers[kind] ?? renderRightPanelRegion,
        }) as const,
    ),
    {
      kind: "files",
      scope: "thread",
      identityVersion: 1,
      persistence: "device",
      lifetime: "active",
      decode: decodeRightPanelSurface,
      acquire: acquireOpenSurfaceLease,
      render: renderers.files ?? renderRightPanelRegion,
    },
    {
      kind: "pull-request",
      scope: "thread",
      identityVersion: 1,
      // Session, not device: which pull requests were open beside a thread is where the reader
      // was, and a restart should return them to the thread rather than to its tabs.
      persistence: "session",
      lifetime: "active",
      decode: decodeRightPanelSurface,
      acquire: acquireOpenSurfaceLease,
      render: renderers["pull-request"] ?? renderRightPanelRegion,
    },
    {
      kind: "workflow",
      scope: "thread",
      identityVersion: 1,
      // Session, like a pull request tab: an open Workflow is where the reader was.
      persistence: "session",
      lifetime: "active",
      decode: decodeRightPanelSurface,
      acquire: acquireOpenSurfaceLease,
      render: renderers.workflow ?? renderRightPanelRegion,
    },
    {
      kind: "experiment",
      scope: "thread",
      identityVersion: 1,
      // Session, like a Workflow tab: the open Experiment is where the reader was.
      persistence: "session",
      lifetime: "active",
      decode: decodeRightPanelSurface,
      acquire: acquireOpenSurfaceLease,
      render: renderers.experiment ?? renderRightPanelRegion,
    },
  ] satisfies readonly WorkspaceSurfaceDefinition<RightPanelSurface>[];
}

export const RIGHT_PANEL_SURFACE_DEFINITIONS = createRightPanelSurfaceDefinitions();
